import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/shared/api/client';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Download, Filter, RotateCcw, ThermometerSun, Sun, Droplets } from 'lucide-react';
import Breadcrumb from '@/shared/components/Breadcrumb';
import { downloadCsv } from '@/shared/utils/download';
import { ASSET_HEX_COLORS as ASSET_COLORS, ASSET_FULL_LABELS as ASSET_LABELS } from '@/shared/constants/assetTypes';
import useTypesActifs from '@/shared/hooks/useTypesActifs';

const CONN_COLORS = { connecte: '#10B981', deconnecte: '#EF4444', inconnu: '#94A3B8' };

function createIcon(type, status) {
  const fill = ASSET_COLORS[type] || '#64748B';
  const border = CONN_COLORS[status] || '#94A3B8';
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${fill};border:3px solid ${border};box-shadow:0 2px 6px rgba(0,0,0,0.2);"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

export default function MapPage() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersLayer = useRef(null);
  const [installations, setInstallations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filters, setFilters] = useState({ type: '', region: '', statut_connexion: '' });
  const [counts, setCounts] = useState({ total: 0, connecte: 0, deconnecte: 0 });
  // Meme source que la creation d'un actif : un type absent de ce filtre rend ses
  // installations introuvables sur la carte, ce qui les fait passer pour inexistantes.
  const typesActifs = useTypesActifs();

  useEffect(() => {
    api.get('/regions').then(r => setRegions(r.data)).catch(() => {});
  }, []);

  const fetchInstallations = useCallback(async () => {
    const params = {};
    if (filters.type) params.type = filters.type;
    if (filters.region) params.region = filters.region;
    if (filters.statut_connexion) params.statut_connexion = filters.statut_connexion;
    try {
      const res = await api.get('/installations', { params });
      setInstallations(res.data);
      setCounts({
        total: res.data.length,
        connecte: res.data.filter(i => i.statut_connexion === 'connecte').length,
        deconnecte: res.data.filter(i => i.statut_connexion === 'deconnecte').length,
      });
    } catch (err) {
      console.error(err);
    }
  }, [filters]);

  useEffect(() => { fetchInstallations(); }, [fetchInstallations]);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    mapInstance.current = L.map(mapRef.current, { zoomControl: true }).setView([46.603354, 2.2], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap France', maxZoom: 19,
    }).addTo(mapInstance.current);
    markersLayer.current = L.layerGroup().addTo(mapInstance.current);
    return () => { mapInstance.current?.remove(); mapInstance.current = null; };
  }, []);

  useEffect(() => {
    if (!markersLayer.current) return;
    markersLayer.current.clearLayers();
    installations.forEach(inst => {
      if (!inst.latitude || !inst.longitude) return;
      const marker = L.marker([inst.latitude, inst.longitude], {
        icon: createIcon(inst.asset_type, inst.statut_connexion),
      });
      const clientId = inst.client_id;
      marker.bindPopup(`
        <div style="min-width:200px;font-family:Satoshi,sans-serif;">
          <div style="font-weight:600;font-size:14px;margin-bottom:4px;">${inst.client_nom || 'N/A'}</div>
          <div style="font-size:12px;color:#64748B;margin-bottom:8px;">${inst.client_ville || ''}</div>
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
            <span style="width:8px;height:8px;border-radius:50%;background:${ASSET_COLORS[inst.asset_type]};display:inline-block;"></span>
            <span style="font-size:12px;font-weight:500;">${ASSET_LABELS[inst.asset_type] || inst.asset_type}</span>
          </div>
          <div style="font-size:11px;color:#64748B;">${inst.marque} ${inst.modele} - ${inst.puissance_kw} kW</div>
          <div style="margin-top:6px;display:flex;align-items:center;gap:4px;">
            <span style="width:6px;height:6px;border-radius:50%;background:${CONN_COLORS[inst.statut_connexion]};display:inline-block;"></span>
            <span style="font-size:11px;text-transform:capitalize;">${inst.statut_connexion === 'connecte' ? 'Connecté' : inst.statut_connexion === 'deconnecte' ? 'Déconnecté' : 'Inconnu'}</span>
          </div>
          ${clientId ? `<button data-client-id="${clientId}" class="map-open-client-btn" style="margin-top:8px;width:100%;padding:6px 8px;background:var(--marque-accent);color:#fff;border:0;border-radius:4px;font-size:11px;font-weight:500;cursor:pointer;">Ouvrir la fiche client →</button>` : ''}
        </div>
      `);
      if (clientId) {
        marker.on('popupopen', () => {
          const btn = document.querySelector(`.map-open-client-btn[data-client-id="${clientId}"]`);
          if (btn) {
            btn.onclick = () => navigate(`/clients/${clientId}`);
          }
        });
      }
      markersLayer.current.addLayer(marker);
    });
  }, [installations, navigate]);

  const handleExport = async () => {
    try {
      await downloadCsv('/export/installations/csv');
    } catch (err) { console.error(err); }
  };

  const resetFilters = () => setFilters({ type: '', region: '', statut_connexion: '' });

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="map-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Breadcrumb items={[{ label: 'Carte des actifs' }]} />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Carte des actifs
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="map-results-count">{counts.total} installation{counts.total > 1 ? 's' : ''} affichée{counts.total > 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetFilters} data-testid="reset-filters-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser
          </Button>
          <Button size="sm" className="" onClick={handleExport} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-[160px]" data-testid="filter-type">
                <SelectValue placeholder="Type d'actif" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {typesActifs.map(type => (
                  <SelectItem key={type} value={type}>{ASSET_LABELS[type] || type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.region} onValueChange={(v) => setFilters(f => ({ ...f, region: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-[200px]" data-testid="filter-region">
                <SelectValue placeholder="Région" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les régions</SelectItem>
                {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filters.statut_connexion} onValueChange={(v) => setFilters(f => ({ ...f, statut_connexion: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-[170px]" data-testid="filter-connexion">
                <SelectValue placeholder="Statut connexion" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="connecte">Connecté</SelectItem>
                <SelectItem value="deconnecte">Déconnecté</SelectItem>
                <SelectItem value="inconnu">Inconnu</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex gap-2 ml-auto">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                {counts.connecte} connectés
              </Badge>
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                {counts.deconnecte} déconnectés
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{background:'#3B82F6'}} /> PAC
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{background:'#F59E0B'}} /> PV
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{background:'#F97316'}} /> Ballon
        </span>
        <span className="text-slate-300">|</span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full border-2" style={{borderColor:'#10B981'}} /> Connecté
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full border-2" style={{borderColor:'#EF4444'}} /> Déconnecté
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full border-2" style={{borderColor:'#94A3B8'}} /> Inconnu
        </span>
      </div>

      {/* Map */}
      <Card className="border-slate-200 overflow-hidden">
        <div ref={mapRef} className="w-full" style={{ height: 'calc(100vh - 340px)', minHeight: '400px' }} data-testid="map-container" />
      </Card>
    </div>
  );
}
